'use client';

import React, { useCallback, useEffect, useState } from "react";
import { Loader2, History } from "lucide-react";
import { useTeam, errText, humanizeAction, formatWhen, initialsOf } from "../internals";

// Owner/admin-only activity history. Reads storage.listAuditLog().
export const AuditLog = () => {
  const { storage } = useTeam();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await storage.listAuditLog({ limit: 200 });
      setEntries(list || []);
    } catch (e) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [storage]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <div
        data-testid="audit-loading"
        className="flex items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-16 text-sm text-zinc-500"
      >
        <Loader2 size={16} className="mr-2 animate-spin" />
        Loading activity…
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="audit-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
        {error}
        <button type="button" onClick={load} className="ml-3 underline underline-offset-2 hover:no-underline">
          Try again
        </button>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div
        data-testid="audit-empty-state"
        className="rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-12 text-center"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
          <History size={22} className="text-zinc-500" />
        </div>
        <h3 className="mt-4 text-base font-semibold text-zinc-900">Nothing here yet</h3>
        <p className="mt-1 text-sm text-zinc-500">
          When you invite, change, or remove people, it'll show up here.
        </p>
      </div>
    );
  }

  return (
    <ul data-testid="audit-list" className="space-y-2">
      {entries.map((e) => (
        <li
          key={e._id}
          data-testid={`audit-row-${e._id}`}
          className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white px-3 py-3"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-xs font-semibold text-zinc-600">
            {initialsOf(e.actor_name)}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm text-zinc-800">
              <span className="font-semibold">{e.actor_name || "Someone"}</span>{" "}
              {humanizeAction(e.action)}
              {e.target_name ? <span className="font-semibold"> {e.target_name}</span> : ""}
            </span>
            <span className="mt-0.5 block text-xs text-zinc-400">{formatWhen(e.at)}</span>
          </span>
        </li>
      ))}
    </ul>
  );
};
